import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Button,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  CategoryMultiSelect
} from '../ui';
import StaffSelector, { normalizeGroupRole } from '../admin/StaffSelector';
import { DEFAULT_SPORT, normalizeSportValue } from '../../constants/sports';
import { useDeportes } from '../../hooks/useDeportes';
import DeporteFijo from '../deportes/DeporteFijo';
import {
  updateGroup,
  getGroupStaff,
  assignStaffToGroup,
  removeStaffFromGroup,
  updateStaffRole,
  getCategorias
} from '../../lib/supabase';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';

const GROUP_TYPE_OPTIONS = [
  { value: 'Grupo base', label: 'Grupo base' },
  { value: 'Torneo', label: 'Torneo' },  { value: 'Actividad', label: 'Actividad' },
  { value: 'Otro', label: 'Otro' }
];

const STATUS_OPTIONS = ['Activo', 'Inactivo', 'Suspendido', 'En preparacion', 'Archivado'];
const RESTRICTION_OPTIONS = [
  { value: 'estricta', label: 'Estricta' },
  { value: 'recomendada', label: 'Recomendada' },
  { value: 'sin_restriccion', label: 'Sin restriccion' }
];

const sanitizeCategoryIds = (ids = []) => Array.from(new Set(ids.filter(Boolean)));

const createFormFromGroup = (group = {}) => ({
  nombre_del_grupo: group.nombre_del_grupo || '',
  rama: group.rama || 'Varonil',
  deporte: normalizeSportValue(group.deporte || DEFAULT_SPORT),
  tipo_restriccion: (group.tipo_restriccion || 'estricta').toString().toLowerCase(),
  tipo_de_grupo: group.tipo_de_grupo || 'Grupo base',
  estatus: group.estatus || 'Activo',
  descripcion: group.descripcion || ''
});

const GroupEditorModal = ({
  open,
  group,
  onClose,
  onSaved,
  categories: externalCategories = null,
  showError,
  showSuccess
}) => {
  const [form, setForm] = useState(createFormFromGroup());
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [staffAssignments, setStaffAssignments] = useState([]);

  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(externalCategories || []);
  const [loadingCategories, setLoadingCategories] = useState(!externalCategories);
  const { label: nombreDeporte } = useDeportes();

  const normalizedSport = useMemo(
    () => normalizeSportValue(form.deporte || DEFAULT_SPORT),
    [form.deporte]
  );
  const filteredCategories = useMemo(() => {
    if (!categories?.length) return [];
    return categories.filter(
      (cat) => normalizeSportValue(cat.deporte || DEFAULT_SPORT) === normalizedSport
    );
  }, [categories, normalizedSport]);

  useEffect(() => {
    if (!categories?.length) return;
    const allowedIds = new Set(filteredCategories.map((cat) => cat.id));
    setSelectedCategories((prev) => prev.filter((id) => allowedIds.has(id)));
  }, [categories, filteredCategories]);

  useEffect(() => {
    if (externalCategories) {
      setCategories(externalCategories);
      setLoadingCategories(false);
    }
  }, [externalCategories]);

  useEffect(() => {
    if (!externalCategories && open) {
      fetchCategories();
    }
  }, [externalCategories, open]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const { data, error } = await getCategorias();
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      showError?.('Error', 'No se pudieron cargar las categorias.');
    } finally {
      setLoadingCategories(false);
    }
  }, [showError]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    filteredCategories.forEach((cat) => {
      if (cat?.id) {
        map.set(cat.id, cat);
      }
    });
    return map;
  }, [filteredCategories]);

  const getPrimaryCategoryName = useCallback(
    (ids) => {
      if (!ids?.length) return '';
      const firstValid = ids.find((id) => categoryMap.has(id));
      if (!firstValid) return '';
      return categoryMap.get(firstValid)?.nombre || '';
    },
    [categoryMap]
  );

  useEffect(() => {
    if (!open || !group) return;

    setForm(createFormFromGroup(group));
    const categoryIds = Array.isArray(group.categorias)
      ? group.categorias.map((cat) => cat.id).filter(Boolean)
      : Array.isArray(group.categoria_ids)
        ? group.categoria_ids.filter(Boolean)
        : [];
    setSelectedCategories(categoryIds);
    loadGroupStaff(group.id);
    guard.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, open]);

  const loadGroupStaff = async (groupId) => {
    if (!groupId) return;
    try {

      const { data, error } = await getGroupStaff(groupId);
      if (error) throw error;
      const mapped = (data || []).map((assignment) => ({
        staff_id: assignment.staff_id,
        rol: normalizeGroupRole(assignment.rol),
        staff: assignment.staff,
        tarjeton_entrenador: assignment.tarjeton_entrenador === true,
        tarjeton_asistente: assignment.tarjeton_asistente === true
      }));
      setStaffAssignments(mapped);
    } catch (error) {
      console.error('Error loading group staff:', error);
      showError?.('Error', 'No se pudieron cargar los entrenadores del grupo.');
    } finally {

    }
  };

  // Ojo: estos setters TIENEN que marcar dirty. Antes no lo hacian, asi que se
  // podia editar nombre/rama/deporte/tipo/estatus/descripcion y cerrar el modal
  // sin que la guarda avisara de cambios sin guardar.
  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    guard.markDirty();
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field) => (value) => {
    guard.markDirty();
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const guard = useUnsavedGuard({ onClose: handleClose });

  const handleSave = async () => {
    if (!group?.id) return;

    const trimmedName = form.nombre_del_grupo.trim();
    if (!trimmedName) {
      showError?.('Error', 'El nombre del grupo es obligatorio.');
      return;
    }

    const allowedIds = new Set(filteredCategories.map((cat) => cat.id));
    const categoryIds = sanitizeCategoryIds(selectedCategories).filter((id) => allowedIds.has(id));
    if (categoryIds.length === 0) {
      showError?.('Error', 'Selecciona al menos una categoria para el grupo.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        nombre_del_grupo: trimmedName,
        rama: form.rama,
        tipo_restriccion: form.tipo_restriccion,
        tipo_de_grupo: form.tipo_de_grupo,
        estatus: form.estatus,
        descripcion: form.descripcion?.trim() || null,
        categoria_ids: categoryIds,
        categoria_base: getPrimaryCategoryName(categoryIds) || ''
      };

      const { error, categoriasError } = await updateGroup(group.id, payload);
      if (error) throw error;

      const { data: currentAssignments, error: staffError } = await getGroupStaff(group.id);
      if (staffError) throw staffError;

      const currentIds = (currentAssignments || []).map((assignment) => assignment.staff_id);
      const newIds = staffAssignments.map((assignment) => assignment.staff_id);

      const toAdd = staffAssignments.filter((assignment) => !currentIds.includes(assignment.staff_id));
      const toRemove = (currentAssignments || []).filter((assignment) => !newIds.includes(assignment.staff_id));
      const toUpdate = staffAssignments.filter((assignment) => {
        const existing = (currentAssignments || []).find((item) => item.staff_id === assignment.staff_id);
        if (!existing) return false;
        const roleChanged = normalizeGroupRole(existing.rol) !== normalizeGroupRole(assignment.rol);
        const tarjetonCoachChanged =
          (existing.tarjeton_entrenador === true) !== (assignment.tarjeton_entrenador === true);
        const tarjetonAssistantChanged =
          (existing.tarjeton_asistente === true) !== (assignment.tarjeton_asistente === true);
        return roleChanged || tarjetonCoachChanged || tarjetonAssistantChanged;
      });

      for (const assignment of toAdd) {
        const { error: assignError } = await assignStaffToGroup(
          group.id,
          assignment.staff_id,
          normalizeGroupRole(assignment.rol),
          {
            tarjeton_entrenador: assignment.tarjeton_entrenador === true,
            tarjeton_asistente: assignment.tarjeton_asistente === true
          }
        );
        if (assignError) throw assignError;
      }

      for (const assignment of toRemove) {
        const { error: removeError } = await removeStaffFromGroup(group.id, assignment.staff_id);
        if (removeError) throw removeError;
      }

      for (const assignment of toUpdate) {
        const { error: updateRoleError } = await updateStaffRole(
          group.id,
          assignment.staff_id,
          normalizeGroupRole(assignment.rol),
          {
            tarjeton_entrenador: assignment.tarjeton_entrenador === true,
            tarjeton_asistente: assignment.tarjeton_asistente === true
          }
        );
        if (updateRoleError) throw updateRoleError;
      }

      // Un solo aviso: el padre usa useConfirmDialog, que tiene un solo dialogo, y el exito
      // tapaba al instante el error de las categorias.
      if (categoriasError) {
        showError?.('Categorías sin guardar', `El grupo se guardó, pero sus categorías no: ${categoriasError.message}`);
      } else {
        showSuccess?.('Exito', 'Grupo actualizado correctamente.');
      }
      guard.resetDirty();
      onSaved?.();
      handleClose();
    } catch (error) {
      console.error('Error updating group:', error);
      showError?.(
        'Error',
        error?.message ? `No se pudo actualizar el grupo: ${error.message}` : 'No se pudo actualizar el grupo.'
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedSportLabel = nombreDeporte(form.deporte);

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="grid gap-0 grid-rows-[minmax(0,1fr)_auto] max-w-4xl overflow-hidden p-0">
        <div className="min-h-0 overflow-y-scroll overflow-x-hidden [scrollbar-gutter:stable]">
          <div className="p-6 space-y-5">
            <DialogHeader className="px-0 text-left space-y-1">
              <DialogTitle className="text-xl font-semibold">
                Editar grupo
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Ajusta la informacion principal del grupo y sus categorias.
              </p>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nombre del grupo *</Label>
                <Input
                  value={form.nombre_del_grupo}
                  onChange={handleFieldChange('nombre_del_grupo')}
                  placeholder="Ej. U10 Mixto Flag"
                />
              </div>

              <CategoryMultiSelect
                label="Categorias permitidas"
                description={`Selecciona las categorias validas para este grupo (${selectedSportLabel}).`}
                options={filteredCategories}
                selectedIds={selectedCategories}
                onChange={(ids) => { guard.markDirty(); setSelectedCategories(ids); }}
                sport={form.deporte}
                disabled={loadingCategories}
                emptyMessage={
                  loadingCategories
                    ? 'Cargando categorias...'
                    : 'No hay categorias disponibles. Configuralas desde Administracion > categorias.'
                }
              />

              {/* Los 4 selects van juntos en una sola reja. Antes "Tipo de
                  restriccion" vivia solo en su propio grid-cols-2 y dejaba medio
                  renglon vacio a su derecha. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Rama</Label>
                  <Select value={form.rama} onValueChange={handleSelectChange('rama')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Varonil">Varonil</SelectItem>
                      <SelectItem value="Femenil">Femenil</SelectItem>
                      <SelectItem value="Mixto">Mixto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Deporte</Label>
                  <DeporteFijo nombre={nombreDeporte(form.deporte)} crea="otro grupo" />
                </div>

                <div className="space-y-1.5">
                  <Label>Tipo de grupo</Label>
                  <Select value={form.tipo_de_grupo} onValueChange={handleSelectChange('tipo_de_grupo')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GROUP_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Estatus</Label>
                  <Select value={form.estatus} onValueChange={handleSelectChange('estatus')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Tipo de restriccion</Label>
                  <Select value={form.tipo_restriccion} onValueChange={handleSelectChange('tipo_restriccion')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RESTRICTION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Descripción (opcional)</Label>
                <Textarea
                  rows={3}
                  value={form.descripcion}
                  onChange={handleFieldChange('descripcion')}
                  placeholder="Notas o información relevante del grupo"
                />
              </div>
              <div>
                <StaffSelector
                  selectedStaff={staffAssignments}
                  onChange={(staff) => { guard.markDirty(); setStaffAssignments(staff); }}
                  onError={showError}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t bg-background px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              className="flex-1 sm:flex-none sm:min-w-[9rem]"
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="flex-1 sm:flex-none sm:min-w-[9rem]"
              disabled={saving}
            >
              {saving && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />} Guardar cambios
            </Button>
          </div>
        </div>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  );
};

export default GroupEditorModal;







